import TextCurrency from '@atoms/TextCurrency';
import { Text, View } from '@components/primitives';
import { getDateInFormatLocale } from '@utils/dateUtils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import getStatusAndColor from './detailsUtils';
import styles from './style';
import BankEyeComponent from '../BankEyeComponent';

type FieldConfig = {
  value: string;
  isDate?: boolean;
  totalWeightOfGold?: boolean;
  isStatus?: boolean;
  isCurrency?: boolean;
  dontShowCurrency?: boolean;
  isGoldPrice?: boolean;
  isBankAccountNumber?: boolean;
  labelKey: string;
};

type DetailsConfig = {
  details: FieldConfig[];
};

type DetailsComponentProps = {
  detailsData: Record<string, any>;
  detailsConfig: DetailsConfig;
};

function shouldReturnValue(
  fieldConfig: FieldConfig,
  detailsData: Record<string, any>,
  t: (key: string) => string,
): JSX.Element {
  let displayValue = detailsData[fieldConfig.value];

  if (fieldConfig.isDate) displayValue = getDateInFormatLocale(displayValue, 'DD-MM-YYYY');

  if (fieldConfig.totalWeightOfGold) displayValue = `${displayValue} gr`;

  if (fieldConfig.isStatus && displayValue) {
    const statusData = getStatusAndColor(displayValue);
    return (
      <Text variant="md-bold" style={[{ color: statusData.color }, styles.textAdj]}>
        {t(statusData.text)}
      </Text>
    );
  }

  if (fieldConfig.isCurrency)
    return (
      <TextCurrency
        amountCurrency={displayValue}
        showCurrencySymbol={!fieldConfig.dontShowCurrency}
        variant="md-bold"
        suffix={fieldConfig.isGoldPrice ? '/gr' : null}
        style={styles.textAdj}
      />
    );

  if (fieldConfig.isBankAccountNumber && displayValue) {
    return <BankEyeComponent value={displayValue} />;
  }

  return (
    <Text variant="md-bold" style={styles.textAdj}>
      {displayValue || '-'}
    </Text>
  );
}

function DetailsComponent({ detailsData, detailsConfig }: DetailsComponentProps): JSX.Element {
  const { t } = useTranslation('common');

  return (
    <View style={styles.container}>
      {detailsConfig.details.map(e => (
        <View style={styles.detailRow} key={e.labelKey}>
          <Text variant="md-normal">{t(`editTransactions.${e.labelKey}`)}</Text>
          <View style={styles.styleAdj}>{shouldReturnValue(e, detailsData, t)}</View>
        </View>
      ))}
    </View>
  );
}

export default DetailsComponent;
